import Cookies from "js-cookie";
import {DataGrid} from "@mui/x-data-grid";
import {routes, useGetDataList, useSearch} from "../routes/api";
import React, {useContext, useEffect, useLayoutEffect, useRef, useState} from "react";
import {LoadingComponent, NoDataComponent} from "../util/loading";
import {useLocation, useNavigate} from "react-router-dom";
import Button from "@mui/material/Button";
import {Grid, MenuItem, Select} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Typography from "@mui/material/Typography";
import {fileModel, PAGE_SIZE, updateData} from "../util/util";
import NavigateBreadcrumbs from "../components/navBreadcrumbs";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import SearchBox from "../components/searchBox";
import {EnumContext} from "../components/enumContext";
import CustomToolbar from "../components/gridToolbar";

export default function FileList({alias, fileHeaders}) {
    const token = Cookies.get('token');
    const permissions = Cookies.get('permissions') ?
        JSON.parse(Cookies.get('permissions'))[alias] :
        {create: false, edit: false, delete: false, view: false};
    const webAdmin = Cookies.get('webAdmin') === 'T';
    const route = routes[alias];
    const model = fileModel[alias];
    const {enums} = useContext(EnumContext);
    const lastAlias = useRef(alias);

    const [noData, setNoData] = useState(false);
    const viewHeaders = fileHeaders.filter(key => key !== 'id').map((key) =>
        ({
            field: key,
            headerName: model.fields.fields[key].display,
            minWidth: 100,
            flex: 1,
            hide: key === 'id',
            hideable: !['app_name', 'am_name', 'f_name'].includes(key)
        }));

    const {data, loading, statusCode: status, handleCall} = useGetDataList(token, route);
    const {
        data: searchData,
        loading: searchLoading,
        statusCode: searchStatus,
        handleCall: searchCall
    } = useSearch(token, route);

    const [viewData, setViewData] = useState(null);
    const [totalPages, setTotalPages] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from ? location.state.from : {pathname: `/`};

    const [paginationModel, setPaginationModel] = useState({
        page: -1,
        pageSize: PAGE_SIZE,
    });

    const [searchParams, setSearchParams] = useState({});
    const [searching, setSearching] = useState(false);
    const [openSearch, setOpenSearch] = useState(false);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({
        f_feature: false,
        f_content: false,
        f_timeAdded: false,
        app_feature: false,
        app_content: false,
        app_timeAdded: false,
        am_feature: false,
        am_content: false,
        am_timeAdded: false,
    });
    const [filteredData, setFilteredData] = useState([]);

    useLayoutEffect(() => {
        if (location?.state?.from?.pathname?.split('/')[1] === alias || !location?.state?.from?.pathname) {
            setPaginationModel({...paginationModel, page: Number(sessionStorage.getItem(`${alias}Page`))});
            setSearchParams(JSON.parse(sessionStorage.getItem(`${alias}SearchParams`)) || {});
            setSearching(sessionStorage.getItem(`${alias}Searching`) === 'T');
            setOpenSearch(sessionStorage.getItem(`${alias}OpenSearch`) === 'T');
            setColumnVisibilityModel(JSON.parse(sessionStorage.getItem(`${alias}Columns`)) || {
                f_feature: false,
                f_content: false,
                f_timeAdded: false,
                app_feature: false,
                app_content: false,
                app_timeAdded: false,
                am_feature: false,
                am_content: false,
                am_timeAdded: false,
            });
        } else {
            setPaginationModel({...paginationModel, page: 0});
            sessionStorage.setItem(`${alias}Page`, '0');
            sessionStorage.setItem(`${alias}SearchParams`, '{}');
            sessionStorage.setItem(`${alias}Searching`, 'F');
            sessionStorage.setItem(`${alias}OpenSearch`, 'F');
            sessionStorage.setItem(`${alias}Columns`, JSON.stringify({
                f_feature: false,
                f_content: false,
                f_timeAdded: false,
                app_feature: false,
                app_content: false,
                app_timeAdded: false,
                am_feature: false,
                am_content: false,
                am_timeAdded: false,
            }));
        }
    }, []);

    const handleReset = () => {
        setColumnVisibilityModel({
            f_feature: false,
            f_content: false,
            f_timeAdded: false,
            app_feature: false,
            app_content: false,
            app_timeAdded: false,
            am_feature: false,
            am_content: false,
            am_timeAdded: false,
        });
        sessionStorage.setItem(`${alias}Columns`, JSON.stringify({
            f_feature: false,
            f_content: false,
            f_timeAdded: false,
            app_feature: false,
            app_content: false,
            app_timeAdded: false,
            am_feature: false,
            am_content: false,
            am_timeAdded: false,
        }));
    };

    const fetchData = async () => {
        try {
            if (lastAlias.current !== null && lastAlias.current !== alias) {
                setSearchParams({});
                setSearching(false);
                setOpenSearch(false);
                lastAlias.current = alias;
                setPaginationModel((pm) => ({...pm, page: 0}));
                await handleCall({params: {page: 1}});
            } else {
                if (searching) {
                    await searchCall({params: {...searchParams, page: paginationModel.page + 1}});
                } else {
                    await handleCall({params: {page: paginationModel.page + 1}});
                }
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }

    useEffect(() => {
        paginationModel.page >= 0 && fetchData();
    }, [paginationModel, alias]);

    useEffect(() => {
        if (status === 204 || data?.count === 0 || data?.results?.length === 0) {
            setNoData(true);
        } else if (data?.results?.length > 0) {
            setViewData(data.results.map(data => updateData(data, null, model.prefix, enums)));
            setTotalPages(Math.ceil(data.count / PAGE_SIZE));
            setNoData(false);
        } else {
            setNoData(false);
        }
    }, [data, fileHeaders, status]);

    useEffect(() => {
        if (searchStatus !== 204 && searchData.results?.length > 0) {
            setFilteredData(searchData.results.map(data => updateData(data, null, model.prefix, enums)));
            setTotalPages(Math.ceil(searchData.count / PAGE_SIZE));
        } else {
            setFilteredData([]);
            setTotalPages(0);
        }
    }, [searchData, searchStatus]);


    const handleRowClick = (param, event) => {
        event.preventDefault();
        window.history.replaceState({from: location}, '');
        navigate(`/${alias}/${param.row.id}`, {state: {from: location}});
    };

    const handlePageSelect = (e) => {
        setPaginationModel((pm) => ({...pm, page: e.target.value}));
        sessionStorage.setItem(`${alias}Page`, e.target.value);
    }

    const handlePaginationModelUpdate = (model, details) => {
        setPaginationModel(model);
        sessionStorage.setItem(`${alias}Page`, model.page);
    }

    const handleSearchParams = (params) => {
        sessionStorage.setItem(`${alias}SearchParams`, JSON.stringify(params));
        setSearchParams(params);
    }

    const handleColumnVisibilityLabel = (columns) => {
        sessionStorage.setItem(`${alias}Columns`, JSON.stringify(columns));
        setColumnVisibilityModel(columns);
    }

    const handleEntry = (event) => {
        event.preventDefault();
        navigate(`/${alias}/new`, {state: {from: location}});
    };

    const handleSearch = async () => {
        setFilteredData([]);
        setSearching(true);
        sessionStorage.setItem(`${alias}Searching`, 'T')
        setPaginationModel((pm) => ({...pm, page: 0}));
        sessionStorage.setItem(`${alias}Page`, '0');
    }

    const handleOpenSearch = () => {
        setOpenSearch(true);
        sessionStorage.setItem(`${alias}OpenSearch`, 'T');
    }

    const handleCancelSearch = () => {
        if (searching) {
            setPaginationModel((pm) => ({...pm, page: 0}));
            sessionStorage.setItem(`${alias}Page`, '0');
        }
        setOpenSearch(false);
        sessionStorage.setItem(`${alias}OpenSearch`, 'F');
        setSearching(false);
        sessionStorage.setItem(`${alias}Searching`, 'F');
        setFilteredData([]);
        sessionStorage.setItem(`${alias}SearchParams`, '{}');
        setSearchParams({});
    }

    useLayoutEffect(() => {
        if (!(permissions.view || webAdmin)) {
            navigate(from);
        }
    }, []);

    if ((!searching && loading) || (searching && searchLoading)) return <LoadingComponent/>;
    if (noData) return (
        <Grid
            container
            spacing={2}
            sx={{margin: 'auto', mt: 5, width: '90%'}}
            justifyContent="space-between"
        >
            <Grid size={12}/>
            <Grid size={12}>
                <NavigateBreadcrumbs alias={alias} display={model.display}/>
            </Grid>
            <Grid size={9}>
                <Typography variant='h4'>{model.display}s</Typography>
            </Grid>
            {(permissions.create || webAdmin) &&
                <Grid size={3} sx={{display: "flex", justifyContent: "flex-end"}}>
                    <Button component="label"
                            variant="contained"
                            onClick={handleEntry}
                            startIcon={<AddIcon/>}
                    >
                        {`New ${model.display}`}
                    </Button>
                </Grid>}
            <Grid size={12}>
                <NoDataComponent/>
            </Grid>
        </Grid>
    )

    return (
        <Grid
            container
            spacing={2}
            sx={{margin: 'auto', mt: 5, width: '90%'}}
            justifyContent="space-between"
        >
            <Grid size={12}/>
            <Grid size={12}>
                <NavigateBreadcrumbs alias={alias} display={model.display}/>
            </Grid>
            <Grid size={9}>
                <Typography variant='h4'>{model.display}s</Typography>
            </Grid>
            {(permissions.create || webAdmin) &&
                <Grid size={3} sx={{display: "flex", justifyContent: "flex-end"}}>
                    <Button component="label"
                            variant="contained"
                            onClick={handleEntry}
                            startIcon={<AddIcon/>}
                    >
                        {`New ${model.display}`}
                    </Button>
                </Grid>}
            <Grid size={9}/>
            <Grid size={3} sx={{display: "flex", justifyContent: "flex-end"}}>
                <Button
                    component="label"
                    onClick={openSearch ? handleCancelSearch : handleOpenSearch}
                    startIcon={<ManageSearchIcon/>}
                    sx={{mb: 1}}>
                    {openSearch ? "Cancel" : "Filters"}
                </Button>
            </Grid>
            {openSearch && <Grid size={12}>
                <SearchBox
                    prefix={model.prefix}
                    searchParams={searchParams}
                    setSearchParams={handleSearchParams}
                    searchFields={model.searchFields}
                    sendSearch={handleSearch}
                    sx={{margin: 'auto', mt: 2, mb: 5, maxWidth: '100%'}}
                />
            </Grid>}
            <Grid size={12}/>
            {searching && <Grid size={12}>
                <Typography variant='h6'>
                    Filtered Results:
                </Typography>
            </Grid>}
            <Grid size={12}>
                <DataGrid
                    disableColumnFilter
                    columns={viewHeaders}
                    rows={searching ? filteredData : viewData}
                    rowCount={(searching ? searchData.count : data.count) || 0}
                    paginationMode="server"
                    slots={{toolbar: CustomToolbar}}
                    showToolbar
                    paginationModel={paginationModel}
                    onPaginationModelChange={handlePaginationModelUpdate}
                    pageSizeOptions={[PAGE_SIZE]}
                    rowHeight={80}
                    onRowClick={handleRowClick}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={handleColumnVisibilityLabel}
                    slotProps={{
                        columnsManagement: {
                            disableResetButton: true,
                        },
                        toolbar: {handleReset: handleReset},
                    }}
                />
            </Grid>
            {Boolean(totalPages) &&
                <Grid size={12} sx={{display: "flex", justifyContent: "flex-end", alignItems: 'center'}}>
                    <Typography sx={{mr: 1}}>Page: </Typography>
                    <Select
                        name='Page'
                        key='page'
                        onChange={handlePageSelect}
                        value={paginationModel.page}
                        sx={{
                            minWidth: 70,
                            '& .MuiSelect-select': {
                                backgroundColor: '#f9f9f9',
                            },
                        }}
                    >
                        {Array.from({length: totalPages}, (_, index) => (
                            <MenuItem key={index} value={index}>
                                {index + 1}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>}
        </Grid>
    )
}